import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Save, RotateCcw, Trash2, Download, Upload, AlertTriangle, ChevronDown, ChevronRight, ListTree, Briefcase, Building2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import SimpleTaxonomyListEditor from "@/components/SimpleTaxonomyListEditor";

/**
 * Finance Taxonomy Settings — Department → Category → Sub-category → Cost Type.
 * The tree is now DB-backed (Phase 84.14). Anyone granted `finance_edit_tax_rates`
 * (or the broader `finance_edit` bucket) can add, rename, delete rows and
 * reset to empty/defaults. Every mutation goes through
 * PUT /api/finance/taxonomy (full-replace) so the payload always matches the
 * on-screen state.
 */
export default function FinanceTaxonomySettings() {
  const { user } = useAuth();
  const can = usePermissions();
  const canEdit = can("finance_edit_tax_rates") || can("finance_edit");
  // Reset-to-defaults/wipe is strictly Founder-only on the backend (see
  // POST /finance/taxonomy/reset — Depends(require_founder)) — deliberately
  // not part of the finance_edit_tax_rates delegation.
  const isFounder = user?.role === "founder";

  const [tuples, setTuples] = useState([]);
  const [initial, setInitial] = useState([]);
  const [aopOptions, setAopOptions] = useState([]);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [expandedDept, setExpandedDept] = useState(null);
  // Phase 100 — Master Taxonomy Settings: one page, 3 lists.
  const [activeSection, setActiveSection] = useState("finance");

  const [newDept, setNewDept] = useState("");
  const [newCat, setNewCat] = useState({}); // {dept: value}
  const [newSub, setNewSub] = useState({}); // {"dept::cat": {sub, cost_type}}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/categories");
      const tree = data.tree || [];
      const flat = [];
      for (const d of tree) {
        for (const c of d.categories || []) {
          for (const s of c.sub_categories || []) {
            flat.push({
              department: d.department,
              category: c.category,
              sub_category: s.sub_category,
              cost_type: s.cost_type,
            });
          }
        }
      }
      setTuples(flat);
      setInitial(JSON.parse(JSON.stringify(flat)));
      setAopOptions(data.aop_options || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const out = {};
    for (const t of tuples) {
      const d = t.department;
      const c = t.category;
      out[d] = out[d] || {};
      out[d][c] = out[d][c] || [];
      out[d][c].push(t);
    }
    return out;
  }, [tuples]);

  const departments = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  const dirty = useMemo(
    () => JSON.stringify(tuples) !== JSON.stringify(initial),
    [tuples, initial]
  );

  const addDepartment = () => {
    const name = newDept.trim();
    if (!name) return;
    if (departments.includes(name)) { toast.error("Department already exists"); return; }
    // Create one placeholder row so the department renders. User will edit it.
    setTuples([...tuples, {
      department: name, category: "New category",
      sub_category: "New subcategory", cost_type: aopOptions[0] || "OpEx",
    }]);
    setNewDept("");
    setExpandedDept(name);
  };

  const addCategoryTo = (dept) => {
    const name = (newCat[dept] || "").trim();
    if (!name) return;
    if (grouped[dept]?.[name]) { toast.error("Category already exists"); return; }
    setTuples([...tuples, {
      department: dept, category: name,
      sub_category: "New subcategory", cost_type: aopOptions[0] || "OpEx",
    }]);
    setNewCat({ ...newCat, [dept]: "" });
  };

  const addSubcategoryTo = (dept, cat) => {
    const key = `${dept}::${cat}`;
    const draft = newSub[key] || {};
    const name = (draft.sub || "").trim();
    const ct = (draft.cost_type || aopOptions[0] || "OpEx").trim();
    if (!name) return;
    const exists = grouped[dept][cat].some((r) => r.sub_category === name);
    if (exists) { toast.error("Sub-category already exists"); return; }
    setTuples([...tuples, { department: dept, category: cat, sub_category: name, cost_type: ct }]);
    setNewSub({ ...newSub, [key]: { sub: "", cost_type: ct } });
  };

  const removeRow = (t) => {
    setTuples(tuples.filter((r) => !(
      r.department === t.department && r.category === t.category && r.sub_category === t.sub_category
    )));
  };

  const updateRow = (target, patch) => {
    setTuples(tuples.map((r) => (
      r.department === target.department && r.category === target.category && r.sub_category === target.sub_category
        ? { ...r, ...patch }
        : r
    )));
  };

  const renameDepartment = (oldName, newName) => {
    if (!newName || oldName === newName) return;
    if (departments.includes(newName)) { toast.error("Name already exists"); return; }
    setTuples(tuples.map((r) => (r.department === oldName ? { ...r, department: newName } : r)));
  };

  const renameCategory = (dept, oldCat, newCat) => {
    if (!newCat || oldCat === newCat) return;
    if (grouped[dept]?.[newCat]) { toast.error("Category already exists in this department"); return; }
    setTuples(tuples.map((r) => (
      r.department === dept && r.category === oldCat ? { ...r, category: newCat } : r
    )));
  };

  const removeDepartment = (dept) => {
    if (!window.confirm(`Remove "${dept}" and all its ${Object.values(grouped[dept]).flat().length} rows?`)) return;
    setTuples(tuples.filter((r) => r.department !== dept));
  };

  const removeCategory = (dept, cat) => {
    if (!window.confirm(`Remove "${dept} > ${cat}" and its ${grouped[dept][cat].length} rows?`)) return;
    setTuples(tuples.filter((r) => !(r.department === dept && r.category === cat)));
  };

  const save = async () => {
    // Filter out placeholder rows that user forgot to rename — they'd cause dupes.
    const clean = tuples.map((t) => ({
      department: t.department.trim(),
      category: t.category.trim(),
      sub_category: t.sub_category.trim(),
      cost_type: (t.cost_type || "OpEx").trim(),
    })).filter((t) => t.department && t.category && t.sub_category && t.cost_type);
    const needsConfirm = clean.length < initial.length;
    setSaving(true);
    try {
      const body = { tuples: clean };
      if (needsConfirm) {
        if (!window.confirm(`Tree is shrinking from ${initial.length} to ${clean.length} rows. Continue?`)) {
          setSaving(false);
          return;
        }
        body.confirm = "REPLACE";
      }
      const { data } = await api.put("/finance/taxonomy", body);
      toast.success(`Saved · ${data.row_count} rows`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const runAudit = async () => {
    setAuditing(true);
    try {
      const { data } = await api.get("/finance/taxonomy/audit");
      setAudit(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAuditing(false); }
  };

  const resetTo = async (mode) => {
    const msg = mode === "empty"
      ? "This will WIPE the entire taxonomy. Existing Expense/PR/Asset/Invoice rows will show orphaned categories until you re-populate the tree. Continue?"
      : "Restore the built-in defaults (Analogy Studio's 140-row seed)? Any custom rows you added will be lost.";
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const { data } = await api.post("/finance/taxonomy/reset", { mode, confirm: "RESET" });
      toast.success(`Reset to ${data.mode} · ${data.row_count} rows`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!canEdit) {
    return <div className="p-8 text-sm text-slate-500">Founder/admin only.</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-6 pt-6">
      <PageHeader
        eyebrow="Studio"
        title="Taxonomy settings"
        subtitle="Every list that drives a dropdown across the app — Expense categories, Revenue categories, Employee Designations, Vendor Types."
        actions={
          activeSection !== "finance" ? null :
          <div className="flex items-center gap-2">
            {isFounder && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={runAudit}
                  disabled={auditing}
                  data-testid="taxonomy-audit-btn"
                >
                  {auditing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Download size={14} className="mr-1.5" />}
                  Audit orphans
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => resetTo("empty")}
                  disabled={saving}
                  data-testid="taxonomy-reset-empty-btn"
                >
                  <RotateCcw size={14} className="mr-1.5" /> Reset to empty
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => resetTo("defaults")}
                  disabled={saving}
                  data-testid="taxonomy-reset-defaults-btn"
                >
                  Restore defaults
                </Button>
              </>
            )}
            <Button
              size="sm"
              onClick={save}
              disabled={!dirty || saving}
              className="bg-[#F54800] hover:bg-[#D63E00] text-white"
              data-testid="taxonomy-save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />}
              Save {dirty ? "•" : ""}
            </Button>
          </div>
        }
      />
      </div>

      <div className="max-w-5xl mx-auto p-6 pt-0 space-y-6">

        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden" data-testid="taxonomy-section-switcher">
          <button
            onClick={() => setActiveSection("finance")}
            className={`px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${activeSection === "finance" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            data-testid="taxonomy-section-finance-btn"
          >
            <ListTree size={12} /> Expense Categories
          </button>
          <button
            onClick={() => setActiveSection("revenue")}
            className={`px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${activeSection === "revenue" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            data-testid="taxonomy-section-revenue-btn"
          >
            <Receipt size={12} /> Revenue Categories
          </button>
          <button
            onClick={() => setActiveSection("designations")}
            className={`px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${activeSection === "designations" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            data-testid="taxonomy-section-designations-btn"
          >
            <Briefcase size={12} /> Designations
          </button>
          <button
            onClick={() => setActiveSection("vendor_types")}
            className={`px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${activeSection === "vendor_types" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            data-testid="taxonomy-section-vendor-types-btn"
          >
            <Building2 size={12} /> Vendor Types
          </button>
        </div>

        {activeSection === "revenue" && <RevenueCategoryTreeEditor />}

        {activeSection === "designations" && (
          <SimpleTaxonomyListEditor
            listName="designations"
            title="Designations"
            subtitle="Powers the Designation dropdown in Registry > Employees."
          />
        )}
        {activeSection === "vendor_types" && (
          <SimpleTaxonomyListEditor
            listName="vendor_types"
            title="Vendor Types"
            subtitle={'Powers the Vendor Type dropdown in Registry > Vendors. "Subscription" hides the Statutory/compliance section on that vendor.'}
          />
        )}

        {activeSection === "finance" && (
        <>
        {/* Audit report banner */}
        {audit && (
          <div className="border border-amber-300 bg-amber-50 rounded-sm p-3" data-testid="taxonomy-audit-panel">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <AlertTriangle size={14} />
              {audit.total_orphans} record{audit.total_orphans === 1 ? "" : "s"} reference a category/sub-category not in this tree
            </div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              {Object.entries(audit.by_collection || {}).map(([coll, v]) => (
                <div key={coll} className="bg-white border border-amber-200 rounded-sm px-2 py-1.5">
                  <div className="text-slate-500">{coll}</div>
                  <div className="font-mono text-slate-900">{v.orphan_count}</div>
                </div>
              ))}
            </div>
            <button
              className="mt-2 text-[11px] text-slate-500 hover:text-slate-800"
              onClick={() => setAudit(null)}
            >Dismiss</button>
          </div>
        )}

        {/* Add department */}
        <div className="flex items-center gap-2">
          <Input
            className="max-w-xs"
            placeholder="+ Add department (e.g. Ops)"
            value={newDept}
            onChange={(e) => setNewDept(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addDepartment()}
            data-testid="taxonomy-new-dept-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={addDepartment}
            disabled={!newDept.trim()}
            data-testid="taxonomy-add-dept-btn"
          >
            <Plus size={14} className="mr-1.5" /> Add
          </Button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500 flex items-center justify-center">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading taxonomy…
          </div>
        ) : departments.length === 0 ? (
          <div className="p-12 text-center text-slate-500 border border-dashed border-slate-300 rounded-sm">
            Tree is empty. Add a department above, or click <em>Restore defaults</em> for the built-in seed.
          </div>
        ) : (
          <div className="space-y-3" data-testid="taxonomy-tree">
            {departments.map((dept) => {
              const isOpen = expandedDept === dept;
              const cats = Object.keys(grouped[dept] || {}).sort();
              const totalRows = Object.values(grouped[dept] || {}).flat().length;
              return (
                <div key={dept} className="border border-slate-200 rounded-sm bg-white" data-testid={`taxonomy-dept-${dept}`}>
                  <div className="flex items-center px-3 py-2 bg-slate-50 border-b border-slate-200">
                    <button
                      onClick={() => setExpandedDept(isOpen ? null : dept)}
                      className="flex items-center gap-2 flex-1 text-left font-medium text-slate-900"
                      data-testid={`taxonomy-dept-toggle-${dept}`}
                    >
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <Input
                        value={dept}
                        onChange={(e) => renameDepartment(dept, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-7 max-w-xs font-medium"
                      />
                      <span className="text-xs text-slate-500 font-normal">{cats.length} cat · {totalRows} row</span>
                    </button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => removeDepartment(dept)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`taxonomy-remove-dept-${dept}`}
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>

                  {isOpen && (
                    <div className="p-3 space-y-3">
                      {cats.map((cat) => {
                        const rows = grouped[dept][cat];
                        return (
                          <div key={cat} className="border border-slate-200 rounded-sm">
                            <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50/50 border-b border-slate-200">
                              <Input
                                value={cat}
                                onChange={(e) => renameCategory(dept, cat, e.target.value)}
                                className="h-7 max-w-xs text-sm"
                              />
                              <span className="text-xs text-slate-500">{rows.length} sub</span>
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => removeCategory(dept, cat)}
                                className="ml-auto text-red-500 hover:text-red-700"
                              >
                                <Trash2 size={12} />
                              </Button>
                            </div>
                            <div className="p-2 space-y-1.5">
                              {rows.map((r) => (
                                <div key={r.sub_category} className="flex items-center gap-2">
                                  <Input
                                    value={r.sub_category}
                                    onChange={(e) => updateRow(r, { sub_category: e.target.value })}
                                    className="h-7 text-xs flex-1"
                                  />
                                  <Select
                                    value={r.cost_type}
                                    onValueChange={(v) => updateRow(r, { cost_type: v })}
                                  >
                                    <SelectTrigger className="h-7 w-40 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {aopOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    size="sm" variant="ghost"
                                    onClick={() => removeRow(r)}
                                    className="text-slate-400 hover:text-red-600"
                                  >
                                    <Trash2 size={11} />
                                  </Button>
                                </div>
                              ))}
                              {/* Add subcategory */}
                              <div className="flex items-center gap-2 pt-1">
                                <Input
                                  placeholder="+ Sub-category"
                                  value={newSub[`${dept}::${cat}`]?.sub || ""}
                                  onChange={(e) => setNewSub({
                                    ...newSub,
                                    [`${dept}::${cat}`]: {
                                      ...(newSub[`${dept}::${cat}`] || {}),
                                      sub: e.target.value,
                                    },
                                  })}
                                  onKeyDown={(e) => e.key === "Enter" && addSubcategoryTo(dept, cat)}
                                  className="h-7 text-xs flex-1"
                                />
                                <Select
                                  value={newSub[`${dept}::${cat}`]?.cost_type || aopOptions[0] || "OpEx"}
                                  onValueChange={(v) => setNewSub({
                                    ...newSub,
                                    [`${dept}::${cat}`]: {
                                      ...(newSub[`${dept}::${cat}`] || {}),
                                      cost_type: v,
                                    },
                                  })}
                                >
                                  <SelectTrigger className="h-7 w-40 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {aopOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <Button
                                  size="sm" variant="outline"
                                  onClick={() => addSubcategoryTo(dept, cat)}
                                  disabled={!(newSub[`${dept}::${cat}`]?.sub || "").trim()}
                                >
                                  <Plus size={12} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {/* Add category */}
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="+ Category"
                          value={newCat[dept] || ""}
                          onChange={(e) => setNewCat({ ...newCat, [dept]: e.target.value })}
                          onKeyDown={(e) => e.key === "Enter" && addCategoryTo(dept)}
                          className="h-7 max-w-xs text-sm"
                        />
                        <Button
                          size="sm" variant="outline"
                          onClick={() => addCategoryTo(dept)}
                          disabled={!(newCat[dept] || "").trim()}
                        >
                          <Plus size={12} className="mr-1" /> Add category
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}


/**
 * Phase 140 — Revenue Taxonomy: Main Service Category -> Sub-Service Type,
 * with an HSN Code on every Main Category. Feeds the Global Invoice Wizard's
 * Service Category dropdown + HSN auto-fill everywhere an invoice is made.
 */
function RevenueCategoryTreeEditor() {
  const [categories, setCategories] = useState([]);
  const [initial, setInitial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [newMain, setNewMain] = useState({ main_category: "", hsn_code: "", gst_rate: "", default_description: "" });
  const [newSub, setNewSub] = useState({}); // {mainCategory: value}
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [csvPreview, setCsvPreview] = useState(null); // { added, updated, skipped: [{row, reason}] }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/revenue-categories");
      const cats = data.categories || [];
      setCategories(cats);
      setInitial(JSON.parse(JSON.stringify(cats)));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => JSON.stringify(categories) !== JSON.stringify(initial), [categories, initial]);

  const patchMain = (main, patch) => setCategories((cats) => cats.map((c) => (c.main_category === main ? { ...c, ...patch } : c)));
  const removeMain = (main) => {
    if (!window.confirm(`Remove "${main}" and all its sub-types?`)) return;
    setCategories((cats) => cats.filter((c) => c.main_category !== main));
  };
  const removeSub = (main, sub) => setCategories((cats) => cats.map((c) => (
    c.main_category === main ? { ...c, sub_types: (c.sub_types || []).filter((s) => s !== sub) } : c
  )));
  // Phase 141 — GST Rate + Default Service Description are mandatory on every
  // new Main Category (they auto-fill straight onto the invoice + its PDF).
  const canAddMain = newMain.main_category.trim() && newMain.gst_rate !== "" && newMain.default_description.trim()
    && !categories.some((c) => c.main_category === newMain.main_category.trim());
  const addMain = () => {
    if (!canAddMain) return;
    setCategories((cats) => [...cats, {
      main_category: newMain.main_category.trim(), hsn_code: newMain.hsn_code.trim(),
      gst_rate: Number(newMain.gst_rate), default_description: newMain.default_description.trim(), sub_types: [],
    }]);
    setNewMain({ main_category: "", hsn_code: "", gst_rate: "", default_description: "" });
  };
  const addSub = (main) => {
    const sub = (newSub[main] || "").trim();
    if (!sub) return;
    setCategories((cats) => cats.map((c) => (
      c.main_category === main && !(c.sub_types || []).includes(sub) ? { ...c, sub_types: [...(c.sub_types || []), sub] } : c
    )));
    setNewSub({ ...newSub, [main]: "" });
  };

  // Phase 142 — Bulk Category Import: paste a CSV (name,hsn,gst,description
  // header row) instead of adding Main Categories one at a time. Parsed
  // client-side, merged into the working tree, then the existing "Save"
  // button persists it exactly like any other manual edit.
  const parseCsvRows = (text) => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];
    // Basic RFC4180 split: handles quoted fields containing commas.
    const splitLine = (line) => {
      const out = []; let cur = ""; let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
          if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
          else if (ch === '"') { inQuotes = false; }
          else { cur += ch; }
        } else if (ch === '"') { inQuotes = true; }
        else if (ch === ",") { out.push(cur); cur = ""; }
        else { cur += ch; }
      }
      out.push(cur);
      return out.map((s) => s.trim());
    };
    let rows = lines.map(splitLine);
    const HEADER_KEYWORDS = ["name", "hsn", "gst", "description"];
    if (rows[0] && rows[0].map((c) => c.toLowerCase()).some((c) => HEADER_KEYWORDS.includes(c))) rows = rows.slice(1); // drop header row
    return rows;
  };

  const buildCsvDiff = (text) => {
    const rows = parseCsvRows(text);
    const added = [], updated = [], skipped = [];
    const byName = Object.fromEntries(categories.map((c) => [c.main_category.toLowerCase(), c]));
    rows.forEach((cols, i) => {
      const [name, hsn, gst, description] = cols;
      if (!name?.trim()) { skipped.push({ row: i + 1, reason: "Missing name" }); return; }
      if (gst === undefined || gst === "" || Number.isNaN(Number(gst))) { skipped.push({ row: i + 1, reason: `"${name}" — missing/invalid GST rate` }); return; }
      if (!description?.trim()) { skipped.push({ row: i + 1, reason: `"${name}" — missing description` }); return; }
      const entry = { main_category: name.trim(), hsn_code: (hsn || "").trim(), gst_rate: Number(gst), default_description: description.trim() };
      if (byName[name.trim().toLowerCase()]) updated.push(entry); else added.push(entry);
    });
    return { added, updated, skipped };
  };

  const previewCsv = () => setCsvPreview(buildCsvDiff(csvText));

  const applyCsv = () => {
    const { added, updated } = csvPreview || buildCsvDiff(csvText);
    setCategories((cats) => {
      const next = cats.map((c) => {
        const match = updated.find((u) => u.main_category.toLowerCase() === c.main_category.toLowerCase());
        return match ? { ...c, hsn_code: match.hsn_code || c.hsn_code, gst_rate: match.gst_rate, default_description: match.default_description } : c;
      });
      return [...next, ...added.map((a) => ({ ...a, sub_types: [] }))];
    });
    toast.success(`Imported: ${added.length} added, ${updated.length} updated. Click Save to persist.`);
    setCsvOpen(false); setCsvText(""); setCsvPreview(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/finance/revenue-categories", { categories });
      toast.success("Revenue taxonomy saved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;

  return (
    <div data-testid="revenue-taxonomy-editor">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">Main Service Category → Sub-Service Type. HSN, GST Rate &amp; Service Description all auto-fill into the Global Invoice Wizard (and onto the generated PDF) the moment a Main Category is picked.</p>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={() => setCsvOpen(true)} data-testid="revenue-taxonomy-import-csv-btn">
            <Upload size={13} className="mr-1.5" /> Import CSV
          </Button>
          <Button size="sm" onClick={save} disabled={!dirty || saving} className="bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="revenue-taxonomy-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />} Save {dirty ? "•" : ""}
          </Button>
        </div>
      </div>

      <Dialog open={csvOpen} onOpenChange={(v) => { setCsvOpen(v); if (!v) { setCsvText(""); setCsvPreview(null); } }}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="revenue-taxonomy-csv-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Bulk import Main Categories</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Paste CSV with columns <code className="bg-slate-100 px-1 rounded-sm">name,hsn,gst,description</code> (header row optional).
            An existing category (matched by name) gets its HSN/GST/Description updated; a new name gets added.
          </p>
          <Textarea
            rows={7}
            value={csvText}
            onChange={(e) => { setCsvText(e.target.value); setCsvPreview(null); }}
            placeholder={'name,hsn,gst,description\nBrand Identity,998314,18,"Logo, typography and visual identity system design."'}
            className="text-xs font-mono"
            data-testid="revenue-taxonomy-csv-textarea"
          />
          {csvPreview && (
            <div className="text-xs space-y-1 border border-slate-200 rounded-sm p-2 bg-slate-50" data-testid="revenue-taxonomy-csv-preview">
              <div className="text-emerald-700">{csvPreview.added.length} new categor{csvPreview.added.length === 1 ? "y" : "ies"} to add</div>
              <div className="text-blue-700">{csvPreview.updated.length} existing categor{csvPreview.updated.length === 1 ? "y" : "ies"} to update</div>
              {csvPreview.skipped.length > 0 && (
                <div className="text-amber-700">
                  {csvPreview.skipped.length} row{csvPreview.skipped.length === 1 ? "" : "s"} skipped:
                  <ul className="list-disc ml-4">
                    {csvPreview.skipped.map((s, i) => <li key={i}>Row {s.row}: {s.reason}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {!csvPreview ? (
              <Button onClick={previewCsv} disabled={!csvText.trim()} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="revenue-taxonomy-csv-preview-btn">
                Preview
              </Button>
            ) : (
              <Button onClick={applyCsv} disabled={csvPreview.added.length + csvPreview.updated.length === 0} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="revenue-taxonomy-csv-apply-btn">
                Import {csvPreview.added.length + csvPreview.updated.length} row{csvPreview.added.length + csvPreview.updated.length === 1 ? "" : "s"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="border border-slate-200 rounded-sm divide-y divide-slate-100">
        {categories.map((c) => {
          const open = expanded === c.main_category;
          return (
            <div key={c.main_category}>
              <div className="flex items-center gap-2 px-3 py-2.5" data-testid={`revenue-cat-row-${c.main_category}`}>
                <button onClick={() => setExpanded(open ? null : c.main_category)} className="text-slate-400 hover:text-slate-700">
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                <span className="text-sm font-medium text-slate-900 flex-1">{c.main_category}</span>
                {!c.default_description && <span className="text-[9px] uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-sm">No description</span>}
                <span className="text-[10px] uppercase tracking-wider text-slate-400">GST</span>
                <span className="text-xs font-mono text-slate-600 w-10">{c.gst_rate ?? "—"}{c.gst_rate != null ? "%" : ""}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">HSN</span>
                <span className="text-xs font-mono text-slate-600 w-16">{c.hsn_code || "—"}</span>
                <Button size="sm" variant="ghost" onClick={() => removeMain(c.main_category)} className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 h-7 w-7 p-0" data-testid={`revenue-cat-delete-${c.main_category}`}>
                  <Trash2 size={13} />
                </Button>
              </div>
              {open && (
                <div className="px-10 pb-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">HSN Code</Label>
                      <Input value={c.hsn_code || ""} onChange={(e) => patchMain(c.main_category, { hsn_code: e.target.value })} placeholder="e.g. 998314" className="h-8 text-xs font-mono mt-1" data-testid={`revenue-cat-hsn-${c.main_category}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">GST Tax Rate (%) *</Label>
                      <Input type="number" value={c.gst_rate ?? ""} onChange={(e) => patchMain(c.main_category, { gst_rate: e.target.value === "" ? null : Number(e.target.value) })} placeholder="18" className="h-8 text-xs mt-1" data-testid={`revenue-cat-gst-${c.main_category}`} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Default Service Description *</Label>
                    <Textarea rows={2} value={c.default_description || ""} onChange={(e) => patchMain(c.main_category, { default_description: e.target.value })} placeholder="A 2-sentence description that appears on the generated invoice PDF." className="text-xs mt-1" data-testid={`revenue-cat-description-${c.main_category}`} />
                  </div>

                  <div className="pt-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Sub-Service Types</Label>
                    <div className="space-y-1.5 mt-1.5">
                      {(c.sub_types || []).length === 0 && <div className="text-xs text-slate-400 italic">No sub-types yet</div>}
                      {(c.sub_types || []).map((s) => (
                        <div key={s} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`revenue-sub-row-${c.main_category}-${s}`}>
                          <span className="flex-1">{s}</span>
                          <Button size="sm" variant="ghost" onClick={() => removeSub(c.main_category, s)} className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 h-6 w-6 p-0">
                            <Trash2 size={11} />
                          </Button>
                        </div>
                      ))}
                      <div className="flex items-center gap-2 pt-1">
                        <Input
                          placeholder="+ Sub-Service Type"
                          value={newSub[c.main_category] || ""}
                          onChange={(e) => setNewSub({ ...newSub, [c.main_category]: e.target.value })}
                          onKeyDown={(e) => e.key === "Enter" && addSub(c.main_category)}
                          className="h-7 max-w-xs text-sm"
                          data-testid={`revenue-sub-add-input-${c.main_category}`}
                        />
                        <Button size="sm" variant="outline" onClick={() => addSub(c.main_category)} disabled={!(newSub[c.main_category] || "").trim()} data-testid={`revenue-sub-add-btn-${c.main_category}`}>
                          <Plus size={12} className="mr-1" /> Add
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <div className="px-3 py-3 bg-slate-50/60 space-y-2" data-testid="revenue-cat-add-form">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Add Main Service Category</Label>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Name *" value={newMain.main_category} onChange={(e) => setNewMain({ ...newMain, main_category: e.target.value })} className="h-8 text-sm" data-testid="revenue-cat-add-name" />
            <Input placeholder="HSN Code" value={newMain.hsn_code} onChange={(e) => setNewMain({ ...newMain, hsn_code: e.target.value })} className="h-8 text-sm font-mono" data-testid="revenue-cat-add-hsn" />
            <Input type="number" placeholder="GST Tax Rate (%) *" value={newMain.gst_rate} onChange={(e) => setNewMain({ ...newMain, gst_rate: e.target.value })} className="h-8 text-sm" data-testid="revenue-cat-add-gst" />
          </div>
          <Textarea rows={2} placeholder="Default Service Description * — a 2-sentence description that appears on the generated invoice PDF." value={newMain.default_description} onChange={(e) => setNewMain({ ...newMain, default_description: e.target.value })} className="text-sm" data-testid="revenue-cat-add-description" />
          <Button size="sm" variant="outline" onClick={addMain} disabled={!canAddMain} data-testid="revenue-cat-add-btn">
            <Plus size={12} className="mr-1" /> Add Main Category
          </Button>
        </div>
      </div>
    </div>
  );
}
